import Checkbox from 'components/Checkbox/checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/UI/BaseDropdown/dropdown-base'
import {
  ds2026DropdownContentClass,
  ds2026DropdownItemClass,
} from 'components/FbUI/ds2026DropdownMenu'
import { cn } from 'components/UI/cn'
import React, { useEffect, useRef } from 'react'
import { BsChevronDown, BsX } from 'react-icons/bs'
import { formatInteger } from 'utils/formatting'
import { useTableLinkVariant } from 'components/Tables/CommonTableCells/TableLinkStyleContext'

interface DataTableSelectAllDropdownProps {
  isAllRowsSelected: boolean
  totalSelected: number
  listCount: number
  pageCount: number
  selectAllText?: string
  onSelectAllInList: () => void
  onSelectAllInPage: () => void
  onSelectNone: () => void
}

export const DataTableSelectAllDropdown = ({
  isAllRowsSelected,
  totalSelected,
  listCount,
  pageCount,
  selectAllText,
  onSelectAllInList,
  onSelectAllInPage,
  onSelectNone,
}: DataTableSelectAllDropdownProps) => {
  const variant = useTableLinkVariant()
  // On DS2026 surfaces (Targets), when rows are selected we promote the
  // "Selected (N)" label out of the inline trigger and into the dropdown
  // menu as an informational header (with × close). Trigger collapses to
  // just the caret next to the checkbox.
  const showInlineSelectedCount = !(variant === 'plain' && totalSelected > 0)

  const handleCheckboxChange = () => {
    if (totalSelected > 0) {
      onSelectNone()
    } else {
      onSelectAllInList()
    }
  }

  const ref = useRef<HTMLInputElement>(null!)

  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = !isAllRowsSelected && totalSelected > 0
    }
  }, [totalSelected, isAllRowsSelected])

  const handleCheckboxClick = (event: React.MouseEvent<HTMLInputElement>) => {
    event.stopPropagation()
  }

  const items = [
    {
      label: `All in list (${formatInteger(listCount)})`,
      callback: onSelectAllInList,
    },
    {
      label: `All on page (${formatInteger(pageCount)})`,
      callback: onSelectAllInPage,
    },
    {
      label: 'None',
      callback: onSelectNone,
    },
  ]

  return (
    <div className={showInlineSelectedCount ? 'w-[200px]' : 'w-fit'}>
      <DropdownMenu>
        <div className="justify-start px-[12px]">
          <div className="flex">
            <div className="relative z-10 flex items-center">
              <Checkbox
                checked={isAllRowsSelected}
                onChange={handleCheckboxChange}
                ref={ref}
                onClick={handleCheckboxClick}
              />
            </div>
            <DropdownMenuTrigger asChild>
              <div className="ml-4 flex items-center justify-start gap-2 text-neutral-700">
                {showInlineSelectedCount ? (
                  <span className="inline-flex flex-col text-sm leading-none">
                    {totalSelected > 0 ? (
                      <>
                        <span className="font-semibold">Selected</span>
                        <span className="mt-1 font-light text-xs leading-none">{`(${formatInteger(totalSelected)})`}</span>
                      </>
                    ) : (
                      <span className="font-medium">
                        {selectAllText || 'All'}
                      </span>
                    )}
                  </span>
                ) : (
                  <span className="text-sm leading-none">
                    <span className="font-semibold">Selected</span>
                    <span className="ml-1 font-light">{`(${formatInteger(totalSelected)})`}</span>
                  </span>
                )}
                <BsChevronDown />
              </div>
            </DropdownMenuTrigger>
          </div>
        </div>
        <DropdownMenuContent
          className={cn(ds2026DropdownContentClass, 'tabular-nums')}
        >
          {!showInlineSelectedCount && (
            <div className="flex items-center justify-between border-b border-neutral-200 px-3 py-2">
              <span className="flex items-center text-sm font-semibold text-neutral-900">
                {formatInteger(totalSelected)} selected
              </span>
              <button
                type="button"
                aria-label="Clear selection"
                onClick={onSelectNone}
                className="ml-1 rounded p-0.5 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900"
              >
                <BsX size={18} />
              </button>
            </div>
          )}
          <DropdownMenuGroup>
            {items.map((item) => (
              <DropdownMenuItem
                onClick={item.callback}
                key={item.label}
                className={ds2026DropdownItemClass}
              >
                {item.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
